// ClinicOS Portal — Invoice List Page
// Patient views their invoices with status and download

import { useState, useEffect } from 'react';
import { FileText, Download, CreditCard, AlertCircle, Check, Clock } from 'lucide-react';
import { API_BASE } from '../config/api';

interface Invoice {
    id: string;
    invoiceNumber: string;
    totalCents: string;
    status: string;
    createdAt: string;
    doctor: { firstName: string; lastName: string };
}

const statusConfig: Record<string, { icon: typeof Check; color: string; label: string }> = {
    paid: { icon: Check, color: '#059669', label: 'Pagada' },
    draft: { icon: Clock, color: '#d97706', label: 'Pendiente' },
    sent: { icon: CreditCard, color: '#2563eb', label: 'Enviada' },
    overdue: { icon: AlertCircle, color: '#ef4444', label: 'Vencida' },
};

export function InvoicesPage() {
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            try {
                const ptRes = await fetch(`${API_BASE}/patients?limit=1`);
                const ptJson = await ptRes.json();
                const pt = ptJson.data?.[0];
                const url = pt ? `${API_BASE}/billing/invoices?patientId=${pt.id}&limit=20` : `${API_BASE}/billing/invoices?limit=20`;
                const res = await fetch(url);
                const json = await res.ok ? await res.json() : { data: [] };
                setInvoices(json.data || []);
            } catch { /* fallback to demo */ }
            setLoading(false);
        }
        load();
    }, []);

    // Mock data for demo
    const demoInvoices: Invoice[] = invoices.length ? invoices : [
        { id: '1', invoiceNumber: 'CLI-2026-000001', totalCents: '18000000', status: 'paid', createdAt: '2026-03-01T10:00:00Z', doctor: { firstName: 'Demo', lastName: 'Médico' } },
        { id: '2', invoiceNumber: 'CLI-2026-000002', totalCents: '25000000', status: 'draft', createdAt: '2026-03-03T14:00:00Z', doctor: { firstName: 'Demo', lastName: 'Médico' } },
    ];

    const formatCOP = (cents: string) => {
        const cop = Math.round(Number(cents) / 100);
        return `$${cop.toLocaleString('es-CO')} COP`;
    };

    return (
        <div className="fade-in">
            <div className="portal-header">
                <h1>Mis Facturas</h1>
                <p>Historial de pagos y facturas</p>
            </div>
            <div className="portal-content">
                {loading ? (
                    <div className="portal-card" style={{ padding: 32, textAlign: 'center', color: 'var(--color-text-muted)' }}>
                        Cargando facturas...
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {demoInvoices.map(inv => {
                            const config = statusConfig[inv.status] || statusConfig.draft;
                            const Icon = config.icon;
                            return (
                                <div key={inv.id} className="portal-card" style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <div style={{ width: 42, height: 42, borderRadius: 10, background: `${config.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Icon size={20} style={{ color: config.color }} />
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.invoiceNumber}</div>
                                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                                            Dr. {inv.doctor.firstName} — {new Date(inv.createdAt).toLocaleDateString('es-CO')}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 700, fontSize: 14 }}>{formatCOP(inv.totalCents)}</div>
                                        <span style={{ fontSize: 11, color: config.color, fontWeight: 600 }}>{config.label}</span>
                                    </div>
                                    <button title="Descargar PDF" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-primary)', padding: 4 }}>
                                        <Download size={18} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}
